import React, { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Search, ShoppingBag, Menu, X, ChevronDown, Heart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { motion, AnimatePresence } from "framer-motion";
import LiveSearch from "@/components/LiveSearch";
import HomeAnnouncement from "@/components/HomeAnnouncement";
import { MegaCategory, MegaProducts, MobileCategoryItem } from "@/components/MegaMenu";

const NAV_LINKS = [
  { label: "Face Care", to: "/shop/skincare", type: "category", image: "https://customer-assets-jt897jd0.emergentagent.net/wingman/02601a3e-473d-48af-a665-af65d7a6bfe4/attachments/d5d542df5cd3457ebcd703141aaf8f66_8__.png" },
  { label: "Body Care", to: "/shop/body", type: "category", image: "https://customer-assets-jt897jd0.emergentagent.net/wingman/02601a3e-473d-48af-a665-af65d7a6bfe4/attachments/48baa3f5ab1a4a0f815af19f413cc8d3_7__.png" },
  { label: "Hair Care", to: "/shop/hair", type: "category", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/ewojrlrf_haircare.webp" },
  { label: "Makeup", to: "/shop/makeup", type: "category", image: "https://customer-assets-jt897jd0.emergentagent.net/wingman/02601a3e-473d-48af-a665-af65d7a6bfe4/attachments/896182aadedc462987b26e5c26bab431_5__.png" },
  { label: "Fragrance", to: "/shop/fragrance", type: "category", image: "https://customer-assets-jt897jd0.emergentagent.net/wingman/02601a3e-473d-48af-a665-af65d7a6bfe4/attachments/0af0ab05d5df4b789b85a0f114360a64_4__.png" },
  { label: "Tools & Accessories", to: "/shop/beauty-tools", type: "category", image: "https://customer-assets-jt897jd0.emergentagent.net/wingman/02601a3e-473d-48af-a665-af65d7a6bfe4/attachments/85ea4663516447f1b0afed2328d79b11_3__.png" },
  { label: "New", to: "/shop/new-arrivals", type: "products" },
];

const MOBILE_PRIMARY = [
  { label: "Face Care", to: "/shop/skincare" },
  { label: "Body Care", to: "/shop/body" },
  { label: "Hair Care", to: "/shop/hair" },
  { label: "Makeup", to: "/shop/makeup" },
  { label: "Fragrance", to: "/shop/fragrance" },
  { label: "Tools & Accessories", to: "/shop/beauty-tools" },
  { label: "New Arrivals", to: "/shop/new-arrivals" },
  { label: "Bestsellers", to: "/shop/bestsellers" },
  { label: "About Doria", to: "/about" },
];

const MOBILE_SECONDARY = [
  { label: "Track My Order", to: "/track-order" },
  { label: "Shipping & Returns", to: "/shipping-returns" },
  { label: "Contact", to: "/contact" },
  { label: "FAQs", to: "/faqs" },
];

const QUIZ_TONE = "bg-[#333333]/5 hover:bg-[#333333]/10 text-[#333333]";
const ICON_BTN = "w-10 h-10 min-[400px]:w-11 min-[400px]:h-11 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-[#333333]/5 hover:bg-[#333333]/10 transition-colors duration-300";

/* ---- "QUIZ" pill with live dot ---- */
function QuizPill({ onClick, testId, className = "" }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-label="Take our quiz"
      className={`inline-flex items-center gap-2 rounded-full px-3 sm:px-4 font-sans text-[12px] sm:text-[13px] font-medium whitespace-nowrap transition-colors duration-300 ${QUIZ_TONE} ${className}`}
    >
      <span>QUIZ</span>
      <span className="relative inline-flex w-2 h-2" aria-hidden>
        <span className="absolute inset-0 rounded-full bg-[#3CC26E] opacity-60 animate-ping" />
        <span className="relative inline-flex w-2 h-2 rounded-full bg-[#3CC26E]" />
      </span>
    </button>
  );
}

export default function Header({ onOpenAI, onOpenQuiz }) {
  const { count, setIsOpen } = useCart();
  const { ids: wishIds } = useWishlist();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const menuTimeout = useRef(null);

  const openMenu = useCallback((label) => {
    clearTimeout(menuTimeout.current);
    setActiveMenu(label);
  }, []);

  const closeMenu = useCallback(() => {
    menuTimeout.current = setTimeout(() => setActiveMenu(null), 150);
  }, []);

  const activeLink = NAV_LINKS.find((l) => l.label === activeMenu);
  const { pathname } = useLocation();
  const isCurrent = (to) => pathname === to || pathname.startsWith(`${to}/`);
  const onNavBlur = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setActiveMenu(null); };
  const onNavKey = (e) => { if (e.key === "Escape") setActiveMenu(null); };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40" data-testid="site-header">
        <div data-testid="full-nav" className="relative z-40">
        {/* Announcement bar */}
        <HomeAnnouncement />

        {/* Main nav */}
        <div className="bg-white/95 backdrop-blur-md border-b border-[#333333]/08">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 xl:px-10 h-16 flex items-center">
            {/* Left: hamburger (phones/tablets) + logo */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-toggle" className={`lg:hidden ${ICON_BTN}`} aria-label="Menu">
                <Menu className="w-5 h-5 text-[#333333]" />
              </button>
              <Link to="/" data-testid="logo-link" className="flex-shrink-0">
                <span className="font-serif text-[20px] tracking-[0.08em] min-[400px]:text-[22px] min-[400px]:tracking-[0.12em] sm:text-2xl sm:tracking-[0.15em] font-bold text-[#333333]">DORIA</span>
              </Link>
            </div>

            {/* Center: nav links (desktop) */}
            <nav className="hidden lg:flex self-stretch items-stretch justify-center flex-1 gap-0.5 xl:gap-1" data-testid="main-nav">
              {NAV_LINKS.map((link, i) => (
                <div
                  key={link.label}
                  className="relative h-full flex items-center"
                  onMouseEnter={() => openMenu(link.label)}
                  onMouseLeave={closeMenu}
                  onFocus={() => openMenu(link.label)}
                  onBlur={onNavBlur}
                  onKeyDown={onNavKey}
                >
                  <Link
                    to={link.to}
                    aria-haspopup="true"
                    aria-expanded={activeMenu === link.label}
                    aria-current={isCurrent(link.to) ? "page" : undefined}
                    className={`flex items-center gap-1 px-1.5 xl:px-2 2xl:px-3 py-2 text-[11px] xl:text-[12px] 2xl:text-[13px] tracking-[0.01em] xl:tracking-[0.04em] 2xl:tracking-[0.06em] uppercase whitespace-nowrap font-sans font-medium transition-colors duration-300 hover:text-[#333333] ${activeMenu === link.label || isCurrent(link.to) ? "text-[#333333]" : "text-[#333333]/70"}`}
                    data-testid={`nav-${link.label.toLowerCase().replace(/&/g, "and").replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                    {(link.type === "category" || link.type === "products") && (
                      <ChevronDown className={`hidden xl:block w-3 h-3 transition-transform duration-200 ${activeMenu === link.label ? "rotate-180" : ""}`} />
                    )}
                  </Link>
                  {isCurrent(link.to) && (
                    <span
                      className="absolute left-1.5 right-1.5 xl:left-2 xl:right-2 2xl:left-3 2xl:right-3 bottom-0 h-[2px] rounded-full bg-[#111111]"
                      data-testid={`nav-active-${link.label.toLowerCase().replace(/&/g, "and").replace(/\s+/g, "-")}`}
                    />
                  )}
                  <AnimatePresence>
                    {activeMenu === link.label && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className={`absolute top-full z-40 pt-1 ${i >= NAV_LINKS.length - 2 ? "right-0" : "left-0"}`}
                        data-testid="mega-panel"
                      >
                        <div className="bg-white rounded-2xl shadow-2xl shadow-black/10">
                          {link.type === "category" && <MegaCategory link={link} onClose={() => setActiveMenu(null)} />}
                          {link.type === "products" && <MegaProducts link={link} onClose={() => setActiveMenu(null)} />}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </nav>

            {/* Right: quiz + icons */}
            <div className="flex items-center gap-1 sm:gap-2 ml-auto">
              <QuizPill onClick={onOpenQuiz} testId="quiz-pill" className="h-10 min-[400px]:h-11 sm:h-10" />
              <button onClick={() => setSearchOpen((v) => !v)} data-testid="search-toggle" className={ICON_BTN} aria-label="Search">
                <Search className="w-[18px] h-[18px] text-[#333333]" />
              </button>
              <Link to="/wishlist" data-testid="wishlist-link" className={`relative ${ICON_BTN}`} aria-label="Saved items">
                <Heart className="w-[18px] h-[18px] text-[#333333]" />
                {wishIds.length > 0 && <span data-testid="wishlist-count" className="absolute top-1 right-1 bg-[#DCAE96] text-white text-[9px] font-sans font-medium w-4 h-4 rounded-full flex items-center justify-center">{wishIds.length}</span>}
              </Link>
              <button onClick={() => setIsOpen(true)} data-testid="cart-toggle" className={`relative ${ICON_BTN}`} aria-label="Cart">
                <ShoppingBag className="w-[18px] h-[18px] text-[#333333]" />
                {count > 0 && <span data-testid="cart-count" className="absolute top-1 right-1 bg-[#DCAE96] text-white text-[9px] font-sans font-medium w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
              </button>
            </div>
          </div>
        </div>
        </div>

        {/* Mega Menu dim backdrop (panels live under each nav link) */}
        <AnimatePresence>
          {activeLink && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 right-0 h-screen bg-black/20 backdrop-blur-sm z-30"
              data-testid="mega-backdrop"
              onClick={() => setActiveMenu(null)}
            />
          )}
        </AnimatePresence>

        <LiveSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      </header>

      {/* Mobile Drawer */}
      {createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9998] bg-black/30" onClick={() => setMobileOpen(false)} />
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "tween", duration: 0.3 }}
                className="fixed top-0 left-0 bottom-0 z-[9999] w-[85vw] max-w-[400px] bg-[#F7F7F5] shadow-2xl flex flex-col"
                data-testid="mobile-menu"
              >
                <div className="px-6 py-5 flex items-center justify-between">
                  <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" className="p-1"><X className="w-5 h-5 text-[#333333]" /></button>
                  <span className="font-serif text-xl tracking-tight font-bold text-[#333333]">DORIA</span>
                  <div className="w-7" />
                </div>

                <nav className="flex-1 overflow-y-auto px-6">
                  <div className="space-y-1 mb-8">
                    {MOBILE_PRIMARY.map((item) => (
                      <MobileCategoryItem key={item.label} item={item} active={isCurrent(item.to)} onNavigate={() => setMobileOpen(false)} />
                    ))}
                  </div>

                  <div className="border-t border-[#333333]/10 pt-6 space-y-3">
                    {MOBILE_SECONDARY.map((item) => (
                      <Link
                        key={item.label}
                        to={item.to}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`mobile-secondary-${item.label.toLowerCase().replace(/&/g, "and").replace(/[^a-z]+/g, "-")}`}
                        className="block py-1 font-sans text-[15px] text-[#333333]/70 hover:text-[#333333] transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </nav>

                <div className="px-6 py-5 border-t border-[#333333]/10">
                  <p className="font-sans text-xs text-[#333333]/30 text-center tracking-wider uppercase">Beauty, reimagined.</p>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
